import React from "react";
import { motion } from "framer-motion";
import {
  Receipt,
  Utensils,
  Wallet,
  BarChart3,
  Smartphone,
  ShieldCheck,
} from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";

const ICONS = [Receipt, Utensils, Wallet, BarChart3, Smartphone, ShieldCheck];
const COLORS = [
  { border: "card-border-orange", glow: "rgba(249,115,22,0.22)", tint: "text-orange-400", bg: "bg-orange-500/10" },
  { border: "card-border-green", glow: "rgba(34,197,94,0.22)", tint: "text-emerald-400", bg: "bg-emerald-500/10" },
  { border: "card-border-yellow", glow: "rgba(234,179,8,0.22)", tint: "text-amber-400", bg: "bg-amber-500/10" },
  { border: "card-border-blue", glow: "rgba(59,130,246,0.22)", tint: "text-blue-400", bg: "bg-blue-500/10" },
  { border: "card-border-pink", glow: "rgba(236,72,153,0.22)", tint: "text-pink-400", bg: "bg-pink-500/10" },
  { border: "card-border-teal", glow: "rgba(20,184,166,0.22)", tint: "text-teal-400", bg: "bg-teal-500/10" },
];

export default function Features() {
  const { t } = useLang();

  const handleMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  return (
    <section id="features" className="relative py-20 md:py-32">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <SectionHeader eyebrow={t.features.eyebrow} title={t.features.title} subtitle={t.features.subtitle} />

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {t.features.cards.map((card, i) => {
            const Icon = ICONS[i];
            const c = COLORS[i];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.55, delay: i * 0.06, ease: "easeOut" }}
                onMouseMove={handleMove}
                style={{ "--glow": c.glow }}
                className={`feature-card rounded-3xl bg-surface/60 border border-white/[0.06] p-7 md:p-8 ${c.border}`}
                data-testid={`feature-card-${i}`}
              >
                <div className={`relative w-12 h-12 rounded-2xl ${c.bg} flex items-center justify-center mb-5`}>
                  <Icon className={c.tint} size={24} strokeWidth={1.75} />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2.5 tracking-tight">
                  {card.title}
                </h3>
                <p className="text-sm md:text-[15px] text-white/60 leading-relaxed">
                  {card.body}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function SectionHeader({ eyebrow, title, subtitle, align = "center" }) {
  const isCenter = align === "center";
  return (
    <div className={isCenter ? "text-center max-w-2xl mx-auto" : "max-w-2xl"}>
      <motion.span
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="inline-block text-xs uppercase tracking-[0.22em] text-brand-orange/90 font-medium"
      >
        {eyebrow}
      </motion.span>
      <motion.h2
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.05 }}
        className="mt-3 text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tighter text-white leading-tight"
      >
        {title}
      </motion.h2>
      {subtitle && (
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-4 text-base md:text-lg text-white/60 leading-relaxed"
        >
          {subtitle}
        </motion.p>
      )}
    </div>
  );
}
